import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  AuthUser,
  fetchCurrentUser,
  login as apiLogin,
  logout as apiLogout,
} from '../services/auth'

interface AuthContextValue {
  user: AuthUser | null
  /** True while the initial session check is in flight on app start. */
  loading: boolean
  login: (email: string, password: string) => Promise<AuthUser>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetchCurrentUser()
      .then((u) => {
        if (!cancelled) setUser(u)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // When the API layer detects an expired session (any 401 from a
  // non-auth endpoint), clear local user state — RequireAuth then bounces
  // the user to /login on the next render.
  useEffect(() => {
    const handler = () => setUser(null)
    window.addEventListener('xosa-origin:session-expired', handler)
    return () => window.removeEventListener('xosa-origin:session-expired', handler)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const u = await apiLogin(email, password)
    setUser(u)
    return u
  }, [])

  const logout = useCallback(async () => {
    await apiLogout()
    setUser(null)
  }, [])

  const refreshUser = useCallback(async () => {
    const u = await fetchCurrentUser()
    setUser(u)
  }, [])

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser }),
    [user, loading, login, logout, refreshUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}

/** The signed-in user's id, or null outside <AuthProvider> or before sign-in. */
export function useCurrentUserId(): number | null {
  return useContext(AuthContext)?.user?.id ?? null
}
